import React, { useCallback, useEffect, useMemo, useRef } from "react";
import styled from "@mui/material/styles/styled";
import { usePatient } from "../state/patientContext";
import { ActionButton, FlexEndWrapper } from "./uiControllers/CommonStyled";
import { ChatElementView } from "./chatElementView";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { usePatientRouteChanger } from "../PatientRoutes/routeChanger";
import { PatientRoute } from "../PatientRoutes/PatientRoute";
import { SIDE_PADDING } from "./consts/sizeConsts";
import { useNavigation } from "../state/navigationContext";
import { PatientColorMap } from "../layout/patientColorMap";
import { useWidgetSettings } from "../widget/useWidgetSettings";
import { WidgetAlignment } from "../d";
import { usePatientFeatureFlags } from "../hooks/usePatientFeatureFlags";
import { isProduction } from "fe-common/utils/envGetter";
import { GenericButton } from "./uiControllers/genericButton";
import { useClinicData } from "../clinic/clinicHooks";
import { useLocaleContext } from "fe-common/locale/localeContext";
import { BackdropContextProvider } from "../state/stateManagers/useBackdrop";

//region [[ Style ]]
const ChatElementsContainerView = styled("div")({
  display: "flex",
  flexFlow: "column",
  minHeight: "inherit",
});

const ChatAction = styled(ActionButton)({
  minWidth: "auto",
  margin: 0,
});

const ChatElementsList = styled("div")({
  display: "flex",
  flexDirection: "column",
  paddingLeft: SIDE_PADDING,
  paddingRight: SIDE_PADDING,
  paddingTop: "20px",
});

export const MiniActionButton = styled(GenericButton)({
  "&.generic-button": {
    backgroundColor: PatientColorMap.Primary,
    color: "white",
    height: "28px",
    margin: "1px",
    borderRadius: "12px",
  },
});

export const ChatElementWrapper = styled("div")({
  // Padding moved to the question & answers containers to support
  // panels
  // "&:not(:last-of-type)": {
  //   padding: "5px 0",
  // },
  // "&:last-of-type": {
  //   paddingTop: "5px",
  // },
});

const BottomActions = styled(FlexEndWrapper)({
  boxSizing: "border-box",
  width: "100%",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  "& > *": {
    margin: "3px",
  },
});

const Filler = styled("div")({
  height: `calc(100vh - ${isProduction() ? 125 : 150}px)`,
  zIndex: -1,
});
//endregion [[ Style ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

//region [[ Texts ]]

export interface ChatElementsProps {
  className?: string;
}
//endregion [[ Texts ]]

export const ChatElementsContainer = React.memo((props: ChatElementsProps) => {
  const routeChanger = usePatientRouteChanger();
  const { viewChanged } = useNavigation();

  const {
    agreeOnTerms,
    loadingUndo,
    exchanges,
    loading,
    onAnswer,
    completeInfoMessages,
    undo,
    gender,
    caseId,
  } = usePatient();

  const showOpenInExplorer = usePatientFeatureFlags("showOpenInExplorer");
  const resultsButton = usePatientFeatureFlags("resultsButton") as boolean;
  const scrollToLastAnswer = usePatientFeatureFlags("scrollToLastAnswerOnly");

  const {
    name: clinicName,
    clinic: clinicId,
    clinicSettings,
  } = useClinicData();

  const goToSummary = useCallback(() => {
    routeChanger(PatientRoute.Summary);
  }, [routeChanger]);

  const { partnerId, windowAlignment } = useWidgetSettings();

  const { locale } = useLocaleContext();

  useEffect(() => {
    evt({
      category: CATEGORY.PATIENT_CHAT,
      action: ACTION.PATIENT_PAGE_LOAD,
      label: "Chat",
      partnerId,
      props: {
        clinicId,
        clinicName,
        defaultLanguage: clinicSettings?.defaultLocale,
        selectedLanguage:
          locale !== clinicSettings?.defaultLocale ? locale : undefined,
      },
    });
  }, [partnerId, clinicName, clinicId, locale, clinicSettings]);

  const lastElementRef = useRef<HTMLDivElement>();

  const loadingRef = useRef<HTMLDivElement>();

  const resultsRef = useRef<HTMLDivElement>();

  const lastAnsweredIndex = useMemo(() => {
    for (let i = exchanges.length - 1; i >= 0; i--) {
      if (exchanges[i].answer || exchanges[i].info?.length) {
        return i;
      }
    }
    return -1;
  }, [exchanges]);

  // every time we get a new last question, scroll it into view
  useEffect(() => {
    // console.log("scrolling use effect", lastElementRef.current);
    let refToScroll = null;
    if (lastElementRef.current) {
      refToScroll = lastElementRef.current;
    }
    if (!loading && resultsRef.current) {
      refToScroll = resultsRef.current;
    }
    if (!scrollToLastAnswer && loading && loadingRef.current) {
      refToScroll = loadingRef.current;
    }

    if (refToScroll) {
      // set time out to let the keyboard open before scrolling to view
      const scrollToEnd =
        true || !windowAlignment || windowAlignment !== WidgetAlignment.inline;

      setTimeout(() => {
        // console.log("chat elements container - Scrolling to", refToScroll);
        refToScroll.scrollIntoView({
          behavior: "smooth",
          block: scrollToEnd
            ? scrollToLastAnswer
              ? "start"
              : "end"
            : "nearest",
          inline: "end",
        });
      }, 50);
    }
  }, [
    loading,
    exchanges,
    viewChanged,
    windowAlignment,
    scrollToLastAnswer,
    lastAnsweredIndex,
  ]);

  useEffect(() => {
    /* If we didn't agree on terms redirect to the beginning */
    if (agreeOnTerms === false && loading === false) {
      routeChanger(PatientRoute.Welcome);
    }
  }, [agreeOnTerms, loading, routeChanger]);

  return (
    <ChatElementsContainerView
      className={"chat-elements-container " + props.className || ""}
    >
      <BackdropContextProvider>
        <ChatElementsList>
          {[...(exchanges ?? []), null].map((exchange, index) => {
            const isLast = index === exchanges.length - 1;
            const isLastAnswered = lastAnsweredIndex === index;
            const canUndo = isLastAnswered && !loading && !loadingUndo;
            return (
              <ChatElementWrapper
                className={"chat-element-wrapper"}
                ref={isLast && !scrollToLastAnswer ? lastElementRef : null}
                key={index}
              >
                <ChatElementView
                  exchanges={exchanges}
                  onAnswer={isLast && onAnswer}
                  completeInfoMessages={completeInfoMessages}
                  undo={canUndo && undo}
                  exchange={exchange}
                  index={index}
                  gender={gender}
                  ref={
                    scrollToLastAnswer && isLastAnswered ? lastElementRef : null
                  }
                  loading={(isLast && loadingUndo) || (loading && !exchange)}
                />
              </ChatElementWrapper>
            );
          })}
          {scrollToLastAnswer && <Filler>&nbsp;</Filler>}
        </ChatElementsList>
      </BackdropContextProvider>
      <BottomActions>
        {resultsButton && (
          <ChatAction onClick={goToSummary} label="Go to summary" />
        )}
        {resultsButton && (
          <ChatAction
            onClick={() => routeChanger(PatientRoute.Result)}
            label="Results"
          />
        )}
        {showOpenInExplorer && (
          <MiniActionButton
            label="Open in explorer"
            onClick={() => {
              const url = isProduction()
                ? `https://editor.kahun.com/#/w-kahun/wconversationcase/${caseId}`
                : `https://editor-dev.kahun.com/#/w-kahun/wconversationcase/${caseId}`;
              var win = window.open(url, "_blank");
              win?.focus();
            }}
          />
        )}
      </BottomActions>
    </ChatElementsContainerView>
  );
});
